import { resetCopyIds } from './unsaved-copy'
import { tableGrid } from '@w/content-blocks/src/table-html'
import { spliceBlocks, replaceChildren, insertChars, takeInlineTail, deleteRange } from './ops'
// Same parser as blocks.parse; the model barrel also exports browser rendering.
import { mdToBlockDocument } from '@w/content-blocks/src/parser'
import type { Blocks } from '@w/content-blocks/src/block'
import { htmlToBlocks } from './html'
import { insertionFormat, tableShape } from './rich-content'
import type { EditorIntent } from './intents'
import type { CoreState } from './state'
import { inlineContent, splitAtBlocks } from './levels'
import { placeBlocks } from './placement'
import {
  children, clean, gapOf, gapPoint, leaf, locate, plain, pointAt, rows, textOf, type Gap, type Row,
} from './tree'

/** Parse insertion content; identity belongs to the clipboard copy or move producer. `live`: the question ids the page
 * holds, read only by a paste that remaps ids (HTML, Markdown), never for a fragment. */
export function pasteBlocks(intent: Extract<EditorIntent, { type: 'insertFromPaste' }>, code = false,
  live: () => ReadonlySet<string> = () => new Set()): Blocks {
  // A code block takes the clipboard's plain text, never its rich flavours (ProseMirror pastes text/plain into code).
  const plainText = intent.text ?? intent.md
  if (code && plainText) return [{ id: '', type: 'pre', children: [leaf(plainText.replaceAll(/\r\n?/gu, '\n'))] }]
  // Plain-text paste (Mod+Shift+V) keeps Markdown syntax literal (Notion browser check notion-browser-queue.md 32, imp04:
  // `[text "# Title"]` with Shift; ProseMirror input.ts:662 reads the clipboard's plain text then).
  if ('literal' in intent && intent.literal) {
    return intent.md.split(/\r\n?|\n/u).map((line): Blocks[number] => ({ id: '', type: 'p', children: line ? [leaf(line)] : [] }))
  }
  if ('fragment' in intent && intent.fragment) {
    const fragment = structuredClone(intent.fragment)
    const clearIds = (value: unknown): void => {
      if (!value || typeof value !== 'object') return
      if ('type' in value && 'id' in value) value.id = ''
      Object.values(value).forEach(child => { clearIds(child) })
    }
    clearIds(fragment)
    return fragment
  }
  const qids = new Map<string, string>()
  if (intent.html !== undefined) {
    const owned = live()
    return htmlToBlocks(intent.html).map(block => resetCopyIds(block, qids, owned))
  }
  if (code) return [{ id: '', type: 'pre', children: [leaf(intent.md)] }]
  const markdown = intent.md.replaceAll(/(^|\s)(https?:\/\/[^\s<>]+)/gu, '$1[$2]($2)')
  const parsed = mdToBlockDocument(markdown, { withChars: true })
  const fragment: Blocks = []
  let previousEnd = 0
  for (const block of parsed.blocks) {
    const range = parsed.sourceMap.get(block.id)
    if (range) {
      const count = Math.max(0, Math.floor((range.startLine - previousEnd - 2) / 2))
      for (let index = 0; index < count; index++) fragment.push({ id: '', type: 'p', children: [] })
      previousEnd = range.endLine
    }
    fragment.push(block)
  }
  const owned = live()
  return fragment.map(block => resetCopyIds(block, qids, owned))
}

/** Pastes into a row at `offset`, or at a block gap into the gap's own block list. */
export function paste(state: CoreState, target: Row | Gap, offset: number, fragment: Blocks,
  html: boolean): void {
  // A block a pasted text row holds inline stands between the row's halves (INV-LEVEL: text rows hold inline content).
  fragment = splitAtBlocks(fragment)
  if (!('block' in target)) {
    spliceBlocks(state, target.siblings, target.index, 0, ...fragment)
    const last = rows(fragment).at(-1)
    const point = last ? pointAt(last, plain(last.block).length)
      : gapPoint({ ...target, index: target.index + fragment.length })
    state.selection = { anchor: point, focus: point }
    return
  }
  const row = target
  // A field (prompt, option, explanation) is an inline-only slot (INV-LEVEL; Lexical CASE 3a flattens every pasted shape
  // into a slot value): rows join on its line, block-only content (media, dividers) is dropped.
  const inlineField = state.scope !== undefined
  if (inlineField) {
    const content = inlineContent(fragment, false)
    if (content.length === 0) return
    fragment = [{ id: '', type: 'p', children: content }]
  }
  if (row.block.type === 'pre') {
    const text = fragment.map((block) => textOf(block)).join('\n')
    if (!insertChars(state, row.block.children, offset, text, 0)) {
      const tail = takeInlineTail(state, row.block.children, offset)
      spliceBlocks(state, row.block.children, row.block.children.length, 0, leaf(text), ...tail)
    }
    collapse(state, row, offset + text.length)
    return
  }
  // A lone table pasted over cells fills them (Lexical $insertTableIntoGrid); any other fragment in a cell is projected.
  if (row.block.type === 'td' && fragment.length === 1 && fragment[0]?.type === 'table' && (html || state.selection?.table)) {
    const table = row.parents.find((parent) => parent.type === 'table')
    if (table?.type !== 'table') return
    const grid = structuredClone(tableGrid(table))
    const template = tableGrid(fragment[0])
    const range = state.selection?.table && tableShape(grid, state.selection.table)
    const originY = range ? range.from.y
      : grid.findIndex((cells) => cells.some((cell) => cell.id === row.block.id))
    const originX = range ? range.from.x
      : grid[originY].findIndex((cell) => cell.id === row.block.id)
    const height = Math.min(template.length, range ? range.to.y - range.from.y + 1 : Infinity)
    const width = Math.min(Math.max(...template.map((cells) => cells.length)),
      range ? range.to.x - range.from.x + 1 : Infinity)
    const unmerge = new Set<string>()
    for (let y = originY; y < originY + height; y++) {
      for (let x = originX; x < originX + width; x++) {
        const cell = grid[y]?.[x]
        if (cell && (cell.colSpan || cell.rowSpan)) unmerge.add(cell.id)
      }
    }
    const seen = new Set<string>()
    const empty = () => ({ id: '', type: 'td' as const, children: [] as Blocks })
    for (const cells of grid) for (const [x, cell] of cells.entries()) {
      if (!cell || !unmerge.has(cell.id)) continue
      if (seen.has(cell.id)) cells[x] = empty()
      else {
        seen.add(cell.id)
        const copy = { ...cell }
        delete copy.rowSpan
        delete copy.colSpan
        cells[x] = copy
      }
    }
    const columns = Math.max(originX + width, ...grid.map((cells) => cells.length))
    while (grid.length < originY + height) grid.push([])
    for (const cells of grid) for (let x = 0; x < columns; x++) cells[x] ??= empty()
    const pasted = new Set<(typeof template)[number][number]>()
    for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
      const source = template[y][x]
      if (!source || pasted.has(source)) continue
      pasted.add(source)
      const cell = { ...source, id: grid[originY + y][originX + x].id }
      const h = Math.min(source.rowSpan ?? 1, height - y)
      const w = Math.min(source.colSpan ?? 1, width - x)
      if (h > 1) cell.rowSpan = h
      else delete cell.rowSpan
      if (w > 1) cell.colSpan = w
      else delete cell.colSpan
      for (let dy = 0; dy < h; dy++) for (let dx = 0; dx < w; dx++) {
        grid[originY + y + dy][originX + x + dx] = cell
      }
    }
    const emitted = new Set<(typeof grid)[number][number]>()
    const next = grid.map((cells, y) => ({ id: table.children[y]?.id ?? '',
      type: 'tr' as const, children: cells.filter((cell) => {
        if (emitted.has(cell)) return false
        emitted.add(cell)
        return true
      }),
    }))
    replaceChildren(state, table, next)
    const target = rows([table]).find((entry) => entry.block.id === grid[originY][originX].id)
    if (target) collapse(state, target, 0)
    return
  }
  if (row.block.type === 'td') {
    // A cell is an inline-only slot (INV-LEVEL): pasted rows become its lines, block-only content is dropped.
    const content = inlineContent(fragment)
    if (content.length === 0) return
    const length = content.reduce((sum, block) => sum + plain(block).length, 0)
    // Plain text of one line joins the text leaf at the caret, keeping its identity.
    const plainText = content.every(block => (block.type === 'text' || block.type === 'chars') && !('format' in block && block.format))
    if (!plainText || !insertChars(state, row.block.children, offset, content.map(block => plain(block)).join(''), 0)) {
      const tail = takeInlineTail(state, row.block.children, offset)
      spliceBlocks(state, row.block.children, row.block.children.length, 0, ...content, ...tail)
    }
    collapse(state, row, offset + length)
    return
  }
  const paragraph = fragment.length === 1 ? fragment[0] : undefined
  const lineBreak = (block: Blocks[number]) => (block.type === 'text' || block.type === 'chars') && plain(block) === '\n'
  // Plain text of several lines pasted at the end of a heading leaves the heading as it is: the lines become one text
  // block after it, joined by soft line breaks (Notion browser check notion-browser-queue.md 12, imp02: `header "Hello "`
  // / `text "world\nAnd text below"`).
  if (!inlineField && !html && row.block.type === 'h' && offset === plain(row.block).length && paragraph?.type === 'p' &&
    paragraph.children.some(lineBreak)) {
    const lines: Blocks = paragraph.children.map((block) => lineBreak(block) ? { id: '', type: 'br' } : block)
    const text: Blocks[number] = { ...paragraph, children: lines }
    spliceBlocks(state, row.siblings, row.siblings.indexOf(row.block) + 1, 0, text)
    const last = rows([text]).at(-1)
    if (last) collapse(state, last, plain(last.block).length)
    return
  }
  if (!inlineField && paragraph?.type === 'p' && paragraph.children.some(lineBreak)) {
    fragment = []
    let current = { ...paragraph, children: [] as Blocks }
    fragment.push(current)
    for (const block of paragraph.children) {
      if ((block.type === 'text' || block.type === 'chars') && plain(block) === '\n') {
        current = { id: '', type: 'p', children: [] }
        fragment.push(current)
      } else current.children.push(block)
    }
  }
  if (fragment.length === 0) return
  // A paragraph or heading's text joins the row at the caret (an empty row takes a heading as it is); any other block is
  // block content.
  const textRow = (block: Blocks[number] | undefined) => block?.type === 'p' || block?.type === 'h'
  if (fragment.length === 1 && (fragment[0].type === 'p' || textRow(fragment[0]) && row.block.children.length > 0)) {
    const [first] = fragment
    const caret = offset + plain(first).length
    // Plain text takes the marks a typed character would (ProseMirror clipboardTextParser inherits the context's marks).
    const inherited = html ? 0 : insertionFormat(state, row, offset)
    const unformatted = children(first).every((block) => block.type === 'chars' && !block.format)
    const inserted = children(first)
    if (inherited && unformatted) for (const block of inserted) if (block.type === 'chars') block.format = inherited
    spliceInline(state, row.block.children, offset, inserted, !html)
    clean(state, state.blocks)
    collapse(state, row, caret)
    return
  }
  if (html && plain(row.block).slice(0, offset).endsWith('\n\n')) {
    const remainder = takeInlineTail(state, row.block.children, offset)
    deleteRange(state, row.block.children, offset - 2, offset)
    const last = rows(fragment).at(-1)
    const caret = last ? plain(last.block).length : 0
    if (last) last.block.children.push(...remainder)
    spliceBlocks(state, row.siblings, row.siblings.indexOf(row.block) + 1, 0, ...fragment)
    if (last) collapse(state, last, caret)
    return
  }
  // Several paragraphs or headings pasted as blocks at a list item's text row, or one heading at an empty item, leave the
  // item whole: they follow the item outside its list, ending the list there (the items after it continue in a list after
  // them); an empty item takes a first paragraph's text (Notion browser checks notion-browser-queue.md 13, 31 and 64,
  // imp02/imp04/imp07: `bulleted_list "four" / text "Hello" / text "World"`; `[bulleted_list "One"], [text "Two"]`;
  // `bulleted_list ""`, `header "Hello"`).
  const item = row.parents.at(-1)
  const list = row.parents.at(-2)
  const emptyRow = row.block.children.length === 0
  if (html && fragment.every((block) => block.type === 'p' || block.type === 'h') &&
    (fragment.length > 1 || emptyRow && fragment[0]?.type === 'h') && item?.type === 'li' &&
    item.children[0] === row.block && list?.type === 'list' && list.subtype !== 'toggle') {
    const rest = emptyRow && fragment[0]?.type === 'p' ? fragment.slice(1) : fragment
    if (rest !== fragment) spliceBlocks(state, row.block.children, 0, 0, ...children(fragment[0]))
    const owner = row.parents.at(-3)
    const home = owner ? children(owner) : state.blocks
    const following = spliceBlocks(state, list.children, list.children.indexOf(item) + 1, list.children.length)
    const continued: Blocks = following.length > 0 ? [{ ...list, id: '', children: following }] : []
    spliceBlocks(state, home, home.indexOf(list) + 1, 0, ...rest, ...continued)
    const last = rows(rest).at(-1)
    collapse(state, last ?? row, last ? plain(last.block).length : plain(row.block).length)
    return
  }
  // Several blocks pasted into an empty quote nest inside it after its own empty text (Notion browser check
  // notion-browser-queue.md C17, imp07: "pasting two blocks into an empty quote nests both inside the quote, its own text
  // stays empty").
  const quote = row.parents.at(-1)
  if (fragment.length > 1 && quote?.type === 'quote' && quote.children.length === 1 && !plain(row.block) &&
    fragment.every((block) => block.type !== 'li')) {
    spliceBlocks(state, row.siblings, 1, 0, ...fragment)
    // The caret goes where placeBlocks puts it: the end of the last pasted row, else the gap after the pasted blocks.
    const last = rows(fragment).at(-1)
    const gap = last ? undefined : gapOf(state.blocks, { blockId: fragment.at(-1)?.id ?? '', offset: 1 })
    if (!last && !gap) throw new Error('INV-1: a pasted block is missing from its block list')
    const point = last ? pointAt(last, plain(last.block).length) : gapPoint(gap as Gap)
    state.selection = { anchor: point, focus: point }
    return
  }
  // The ownership contract (placement.ts): the row splits at the caret and the fragment lands in the row's own block
  // list; pasted text rows at both ends join the halves.
  const placed = placeBlocks(state, row, offset, fragment, { merge: textRow(fragment[0]) && textRow(fragment.at(-1)), items: true })
  // The caret is held as row and offset across `clean`: merging a pasted code block's line runs removes the run it named.
  const before = rows(state.blocks)
  const at = gapOf(state.blocks, placed) ? undefined : locate(before, placed)
  const owner = at && before[at.row]?.block.id
  clean(state, state.blocks)
  const after = owner ? rows(state.blocks).find((entry) => entry.block.id === owner) : undefined
  const point = after && at ? pointAt(after, at.offset) : placed
  state.selection = { anchor: point, focus: point }
}

function spliceInline(state: CoreState, items: Blocks, offset: number, inserted: Blocks, preserveLink: boolean): void {
  if (preserveLink) {
    const hasLink = (blocks: Blocks): boolean => blocks.some((block) => block.type === 'a' || hasLink(children(block)))
    let remaining = offset
    for (const block of items) {
      const length = plain(block).length
      if (remaining > 0 && remaining < length && block.type === 'a' && !hasLink(inserted)) {
        spliceInline(state, block.children, remaining, inserted, true)
        return
      }
      if (remaining <= length) break
      remaining -= length
    }
  }
  const tail = takeInlineTail(state, items, offset)
  spliceBlocks(state, items, items.length, 0, ...inserted, ...tail)
}

function collapse(state: CoreState, row: Row, offset: number): void {
  const point = pointAt(row, offset)
  state.selection = { anchor: point, focus: point }
}
